import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    WithStyles
} from "@material-ui/core";
import { styles } from "./SubscriptionUserDateRangesDialog.styles";
import * as SubscriptionAction from "../../../stores/subscriptions/SubscriptionAction";
import { IUniversalTableData } from "../../components/UniversalTable/interfaces";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";
import SubscriptionDateRangeModel from "../../../models/subscription/SubscriptionDateRangeModel";
import { consolidateDateToNumber } from "../../../utilities/dateTimeUtils";
import CloseIcon from "@material-ui/icons/Close";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { DateRangesTable } from "../../components/DateRangesTable";

export interface ISubscriptionUserDateRangesDialogProps extends WithStyles<typeof styles> {
    subscriptionId: number;
    subscriptionTitle: string;
    subscriptionComment: string;
    userId: string;
    userName: string;
    onClose: (isUpdated: boolean) => void;
    setTempDateRange?: any;
    isFilterable?: boolean;
}

const SubscriptionUserDateRangesDialogPure: React.FunctionComponent<ISubscriptionUserDateRangesDialogProps> = (
    props: ISubscriptionUserDateRangesDialogProps
) => {
    const {
        onClose,
        subscriptionId,
        subscriptionTitle,
        subscriptionComment,
        userId,
        userName,
        setTempDateRange,
        isFilterable,
        classes
    } = props;

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [isUpdated, setIsUpdated] = useState<boolean>(false);

    useEffect(() => {
        dispatch(SubscriptionAction.requestUserDateRanges(subscriptionId, userId));
    }, [subscriptionId, userId, dispatch]);

    const dateRangesFromState = useSelector(state => state.subscriptions?.dateRanges);
    const isRequestingDateRanges = useSelector(state =>
        selectRequesting(state, [SubscriptionAction.REQUEST_USER_DATE_RANGES])
    );

    let dateRanges: IUniversalTableData;
    if (dateRangesFromState?.ranges) {
        dateRanges = Object.values(dateRangesFromState?.ranges)?.map((dateRange: SubscriptionDateRangeModel) => ({
            id: dateRange.id,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            visibilityStartDate: dateRange.visibilityStartDate,
            visibilityEndDate: dateRange.visibilityEndDate,
            trial: dateRange.trial,
            salesTrial: dateRange.salesTrial,
            actualStartDate: dateRange.actualStartDate,
            subscriptionRequestId: dateRange.subscriptionRequestId,
            levelTwoItems: dateRange.levelTwoItems
        }));
    }

    const handleDeleteFinished = index => {
        dispatch(SubscriptionAction.deleteUserDateRange(subscriptionId, userId, dateRanges[index].id)).then(() => {
            setIsUpdated(true);
            dispatch(SubscriptionAction.requestUserDateRanges(subscriptionId, userId));
            enqueueSnackbar(t("admin.manageSubscriptionsView.subscriptionDeleted"), {
                variant: ToastStatusEnum.Info
            });
        });
    };

    const handleAddDateRange = newData => {
        const newDateRange = {
            ...(newData.startDate && { startDate: consolidateDateToNumber(newData.startDate) }),
            ...(newData.endDate && { endDate: consolidateDateToNumber(newData.endDate) }),
            ...(newData.actualStartDate && { actualStartDate: consolidateDateToNumber(newData.actualStartDate) }),
            ...(newData.visibilityStartDate && { visibilityStartDate: consolidateDateToNumber(newData.visibilityStartDate) }),
            ...(newData.visibilityEndDate && { visibilityEndDate: consolidateDateToNumber(newData.visibilityEndDate) }),
            trial: newData.trial
        };
        if (setTempDateRange) {
            setTempDateRange(newDateRange);
            onClose(isUpdated);
        } else {
            dispatch(SubscriptionAction.addUserDateRange(subscriptionId, userId, newDateRange)).then(() => {
                setIsUpdated(true);
                dispatch(SubscriptionAction.requestUserDateRanges(subscriptionId, userId));
                enqueueSnackbar(t("admin.manageSubscriptionsView.subscriptionAdded"), {
                    variant: ToastStatusEnum.Info
                });
            });
        }
    };

    const handleEditDateRange = (index, newData) => {
        const updatedDateRange = {
            ...(newData.startDate && { startDate: consolidateDateToNumber(newData.startDate) }),
            ...(newData.endDate && { endDate: consolidateDateToNumber(newData.endDate) }),
            ...(newData.actualStartDate && { actualStartDate: consolidateDateToNumber(newData.actualStartDate) }),
            ...(newData.visibilityStartDate && { visibilityStartDate: consolidateDateToNumber(newData.visibilityStartDate) }),
            ...(newData.visibilityEndDate && { visibilityEndDate: consolidateDateToNumber(newData.visibilityEndDate) }),
            trial: newData.trial
        };
        dispatch(
            SubscriptionAction.updateUserDateRange(subscriptionId, userId, dateRanges[index].id, updatedDateRange)
        ).then(() => {
            setIsUpdated(true);
            dispatch(SubscriptionAction.requestUserDateRanges(subscriptionId, userId));
            enqueueSnackbar(t("admin.manageSubscriptionsView.subscriptionEdited"), {
                variant: ToastStatusEnum.Info
            });
        });
    };

    const handleClose = () => {
        onClose(isUpdated);
    };

    return (
        <>
            <Dialog open fullWidth scroll="paper" onClose={handleClose} maxWidth="xl">
                <DialogTitle>
                    <Typography variant="subtitle1">Edit Date Ranges</Typography>
                    <br />
                    {t("general.subscription")}: {subscriptionTitle}
                    <br />
                    {t("general.user")}: {userName}
                    <br />
                    {subscriptionComment && <span className={classes.comment}>Comment: {subscriptionComment}</span>}
                    <IconButton
                        aria-label="close"
                        style={{ position: "absolute", right: "1px", top: "1px" }}
                        onClick={handleClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <div>
                        <DateRangesTable
                            dateRanges={dateRanges}
                            handleEditDateRange={handleEditDateRange}
                            handleAddDateRange={handleAddDateRange}
                            handleDeleteFinished={handleDeleteFinished}
                            isRequestingDateRanges={isRequestingDateRanges}
                            isFilterableSubscription={isFilterable}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <BlueButton onClick={handleClose}>{t("general.close")}</BlueButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export const SubscriptionUserDateRangesDialog = withStyles(styles)(SubscriptionUserDateRangesDialogPure);
